import axios from 'axios';
import { useEffect, useState } from 'react';
import { Badge, Button, FormDrawer, IconButton, Input, Modal, Select, Spin, Switch, useAlert } from '../ui';
import { useAuthHeader, useAuthUser } from 'react-auth-kit';
import { Building2, Calendar, Eye, Hash, Image as ImageIcon, Minus, Plus, QrCode, Save, Trash2, Type, X } from 'lucide-react';
import User from '../../Models/User';
import datas from "../../Assets/data.json";
import env from 'react-dotenv';
import { Crypter } from '../../Functions/Security/Crypter';

/* ------------------------------------------------------------------ */
/*  Types & constantes du layout d'impression (doc ManageLayout)       */
/* ------------------------------------------------------------------ */

type Scope = 'FORM' | 'FILIALE' | 'ABONNE';
type Zone = 'HEADER' | 'FOOTER';
type Align = 'G' | 'C' | 'D';

interface LayoutChamp {
    Zone: Zone;
    Ligne: number;
    Alignement: Align;
    TypeChamp: string;
    Valeur: string;
    Ordre: number;
}

/** Types d'éléments de mise en page — valeur : input | select-tenant | select-image | fixed | none. */
const LAYOUT_TYPES: { value: string; label: string; valeur: 'input' | 'select-tenant' | 'select-image' | 'fixed' | 'none'; icon: JSX.Element }[] = [
    { value: 'TEXTE', label: 'Texte', valeur: 'input', icon: <Type size={13} /> },
    { value: 'IMAGE', label: 'Image / Logo', valeur: 'select-image', icon: <ImageIcon size={13} /> },
    { value: 'TENANT', label: 'Info banque', valeur: 'select-tenant', icon: <Building2 size={13} /> },
    { value: 'DATE', label: 'Date d\'impression', valeur: 'none', icon: <Calendar size={13} /> },
    { value: 'PAGE', label: 'Pagination', valeur: 'none', icon: <Hash size={13} /> },
    { value: 'QRCODE', label: 'QR Code', valeur: 'fixed', icon: <QrCode size={13} /> },
    { value: 'SEPARATEUR', label: 'Séparateur', valeur: 'none', icon: <Minus size={13} /> },
];

const TENANT_VALEURS = [
    { value: 'LIBELLE', label: 'Libellé de la banque' },
    { value: 'LOCATION', label: 'Localisation' },
    { value: 'PHONE', label: 'Téléphone' },
    { value: 'ABONNE', label: 'Code abonné' },
];

const SCOPES: { value: Scope; label: string; hint: string }[] = [
    { value: 'FORM', label: 'Formulaire', hint: 'Layout propre à ce formulaire — priorité maximale' },
    { value: 'FILIALE', label: 'Filiale', hint: 'S\'applique à tous les formulaires de la filiale' },
    { value: 'ABONNE', label: 'Abonné', hint: 'Layout global par défaut — socle de l\'héritage' },
];

const ALIGN_LABEL: Record<Align, string> = { G: 'Gauche', C: 'Centre', D: 'Droite' };

function typeMeta(t: string) {
    return LAYOUT_TYPES.find(x => x.value === t) || LAYOUT_TYPES[0];
}

function defaultValeur(type: string): string {
    if (type === 'IMAGE') return 'LOGO';
    if (type === 'QRCODE') return 'REF';
    if (type === 'TENANT') return 'LIBELLE';
    return '';
}

/** Contenu compact d'un élément (partagé puce éditable / aperçu lecture seule). */
function chipContent(c: LayoutChamp): JSX.Element {
    switch (c.TypeChamp) {
        case 'TEXTE': return <span>{c.Valeur || 'Texte…'}</span>;
        case 'IMAGE': return <span className="inline-flex items-center gap-1"><ImageIcon size={11} /> {c.Valeur === 'LOGO' || !c.Valeur ? 'LOGO' : 'Image'}</span>;
        case 'TENANT': return <span className="inline-flex items-center gap-1"><Building2 size={11} /> {c.Valeur || 'LIBELLE'}</span>;
        case 'DATE': return <span className="inline-flex items-center gap-1"><Calendar size={11} /> {new Date().toLocaleDateString('fr-FR')}</span>;
        case 'PAGE': return <span className="inline-flex items-center gap-1"><Hash size={11} /> 1 / 1</span>;
        case 'QRCODE': return <span className="inline-flex items-center gap-1"><QrCode size={11} /> QR</span>;
        case 'SEPARATEUR': return <Minus size={11} />;
        default: return <span>{c.TypeChamp}</span>;
    }
}

/** Rendu compact d'un élément sur la page (puce cliquable). */
function ChampChip({ c, selected, onClick }: { c: LayoutChamp; selected: boolean; onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onClick(); }}
            className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] transition-all
                ${selected
                    ? 'bg-[var(--eb-primary)] text-white ring-2 ring-[var(--eb-primary)]/40 shadow-sm'
                    : 'bg-[var(--eb-bg)] text-[var(--eb-text)] border border-[var(--eb-border)] hover:border-[var(--eb-primary)]/60'}`}
        >
            {chipContent(c)}
        </button>
    );
}

/** Zone en lecture seule (aperçu global) — mêmes lignes × alignements, sans boutons. */
function StaticZone({ champs, zone, actif }: { champs: LayoutChamp[]; zone: Zone; actif: boolean }) {
    if (!actif) return null;
    const lignes = Array.from(new Set(champs.filter(c => c.Zone === zone).map(c => c.Ligne))).sort((a, b) => a - b);
    if (lignes.length === 0) return null;
    return (
        <>
            {lignes.map(l => (
                <div key={l} className="flex items-center px-2 py-1 min-h-[24px]">
                    {(['G', 'C', 'D'] as Align[]).map(a => (
                        <div key={a} className={`flex-1 flex flex-wrap gap-2 ${a === 'C' ? 'justify-center' : a === 'D' ? 'justify-end' : 'justify-start'}`}>
                            {champs.filter(c => c.Zone === zone && c.Ligne === l && c.Alignement === a)
                                .map((c, i) => <span key={i} className="inline-flex items-center text-[11px] text-[var(--eb-text)]">{chipContent(c)}</span>)}
                        </div>
                    ))}
                </div>
            ))}
        </>
    );
}

/* ------------------------------------------------------------------ */
/*  Éditeur de layout — montage visuel                                 */
/* ------------------------------------------------------------------ */

export default function FormLayoutEditor(props: {
    open: boolean;
    onClose: () => void;
    /** Formulaire fixe (portée FORM verrouillée) — optionnel : sinon select. */
    form?: any;
    /** Ligne LIST à éditer — pré-remplit portée et clés. */
    layout?: any;
    filiales: any[];
    /** Catalogue de formulaires pour le choix CodeForm (Scope=FORM). */
    formulaires: any[];
    onSaved?: () => void;
}) {
    const authHeader = useAuthHeader();
    const auth = useAuthUser();
    const user: User = JSON.parse(JSON.stringify(auth() || {}));
    const alert = useAlert();

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [layoutId, setLayoutId] = useState<number | null>(null);
    const [scope, setScope] = useState<Scope>('FORM');
    const [codeForm, setCodeForm] = useState('');
    const [codeFiliale, setCodeFiliale] = useState('');
    const [headerActif, setHeaderActif] = useState(true);
    const [footerActif, setFooterActif] = useState(true);
    const [champs, setChamps] = useState<LayoutChamp[]>([]);
    const [resolved, setResolved] = useState<any>(null);
    const [openDelete, setOpenDelete] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);

    // Montage : case ciblée pour la palette + élément en cours d'édition
    const [addSlot, setAddSlot] = useState<{ zone: Zone; ligne: number; align: Align } | null>(null);
    const [editIdx, setEditIdx] = useState<number | null>(null);

    useEffect(() => {
        if (!props.open) return;
        const l = props.layout;
        const s: Scope = (['FORM', 'FILIALE', 'ABONNE'].includes(l?.Scope) ? l.Scope : (props.form ? 'FORM' : 'ABONNE')) as Scope;
        const cf = props.form?.CodeForm || l?.CodeForm || '';
        const fl = l?.CodeFiliale || '';
        setScope(s);
        setCodeForm(cf);
        setCodeFiliale(fl);
        setResolved(null);
        setAddSlot(null);
        setEditIdx(null);
        loadScope(s, fl, cf);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.open]);

    function postLayout(payload: any) {
        return axios.request({
            method: 'post',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.Formulaires.ManageLayout,
            headers: { 'Content-Type': 'application/json', 'Authorization': authHeader() },
            data: Crypter.encrypt(JSON.stringify(payload)),
        }).then((r: any) => JSON.parse(Crypter.decrypt(r.data)).Value);
    }

    function getLayout(payload: any) {
        return axios.request({
            method: 'get',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.Formulaires.ManageLayoutGet + Crypter.encrypt(JSON.stringify(payload)),
            headers: { 'Accept': 'text/plain', 'Authorization': authHeader() },
        }).then((r: any) => JSON.parse(Crypter.decrypt(r.data)).Value);
    }

    function scopePayload(s: Scope, filiale: string, cform: string): any {
        const p: any = { CodeAbonne: user.CodeAbonne, Scope: s };
        if (s === 'FORM') p.CodeForm = cform;
        if (s === 'FILIALE') p.CodeFiliale = filiale;
        return p;
    }

    /** Parse les champs d'un Result GET (tableau plat de lignes, {Champs} ou ChampsJson chaîne). */
    function parseChamps(result: any): LayoutChamp[] {
        let list: any[] = [];
        const src = result?.Layout ?? result;
        if (Array.isArray(src?.Champs)) list = src.Champs;
        else if (typeof src?.ChampsJson === 'string') {
            try { const v = JSON.parse(src.ChampsJson); if (Array.isArray(v)) list = v; } catch { }
        }
        else if (Array.isArray(src)) list = src.filter((x: any) => x?.Zone || x?.TypeChamp);
        return list.map((c: any, i: number) => ({
            Zone: (String(c.Zone || 'HEADER').toUpperCase() === 'FOOTER' ? 'FOOTER' : 'HEADER') as Zone,
            Ligne: Math.max(1, Number(c.Ligne) || 1),
            Alignement: (['G', 'C', 'D'].includes(String(c.Alignement || '').toUpperCase()) ? String(c.Alignement).toUpperCase() : 'G') as Align,
            TypeChamp: String(c.TypeChamp || 'TEXTE').toUpperCase(),
            Valeur: c.Valeur ?? '',
            Ordre: c.Ordre ?? i,
        }));
    }

    /** GET — layout de la portée exacte + RESOLVE pour l'héritage affiché. */
    function loadScope(s: Scope, filiale: string, cform: string) {
        setLoading(true);
        setLayoutId(null);
        setHeaderActif(true);
        setFooterActif(true);
        setChamps([]);
        setEditIdx(null);
        setAddSlot(null);

        // Scope FORM sans formulaire choisi : rien à charger
        if (s === 'FORM' && !cform) { setLoading(false); setResolved(null); return; }
        if (s === 'FILIALE' && !filiale) { setLoading(false); setResolved(null); return; }

        const payload = scopePayload(s, filiale, cform);
        getLayout({ ...payload, Option: 'GET' })
            .then((res: any) => {
                const src = res?.Result?.Layout ?? res?.Result;
                if ((res?.StatutCode == 200 || res?.StatutCode === undefined) && src) {
                    // Tableau plat de lignes : l'en-tête (Id, HeaderActif, FooterActif) est sur chaque ligne
                    const head = Array.isArray(src) ? src[0] : src;
                    setLayoutId(head?.LayoutId ?? head?.Id ?? null);
                    setHeaderActif(head?.HeaderActif !== false && head?.HeaderActif !== 0);
                    setFooterActif(head?.FooterActif !== false && head?.FooterActif !== 0);
                    setChamps(parseChamps(res.Result));
                }
            })
            .catch(() => { /* 404 = pas de layout pour cette portée */ })
            .finally(() => setLoading(false));

        if (s === 'FORM' && cform) {
            getLayout({ Option: 'RESOLVE', CodeAbonne: user.CodeAbonne, CodeForm: cform, CodeFiliale: filiale || undefined })
                .then((res: any) => setResolved(res?.Result ?? null))
                .catch(() => setResolved(null));
        } else {
            setResolved(null);
        }
    }

    const updateChamp = (i: number, patch: Partial<LayoutChamp>) =>
        setChamps(prev => prev.map((c, j) => j === i ? { ...c, ...patch } : c));

    const removeChamp = (i: number) => {
        setChamps(prev => prev.filter((_, j) => j !== i));
        setEditIdx(null);
    };

    /** Dépose un élément dans la case ciblée par la palette. */
    const addElement = (zone: Zone, ligne: number, align: Align, type: string) => {
        const nc: LayoutChamp = { Zone: zone, Ligne: ligne, Alignement: align, TypeChamp: type, Valeur: defaultValeur(type), Ordre: champs.length };
        setChamps(prev => [...prev, nc]);
        setAddSlot(null);
        setEditIdx(champs.length);
    };

    function handleSave() {
        if (scope === 'FORM' && !codeForm) { alert.warning('Sélectionnez un formulaire'); return; }
        if (scope === 'FILIALE' && !codeFiliale) { alert.warning('Sélectionnez une filiale'); return; }
        setSaving(true);
        postLayout({
            ...scopePayload(scope, codeFiliale, codeForm),
            Option: 'SET',
            HeaderActif: headerActif,
            FooterActif: footerActif,
            ChampsJson: JSON.stringify(champs.map((c, i) => ({
                Zone: c.Zone, Ligne: c.Ligne, Alignement: c.Alignement,
                TypeChamp: c.TypeChamp, Valeur: c.Valeur || undefined, Ordre: i,
            }))),
        })
            .then((res: any) => {
                setSaving(false);
                if (res?.StatutCode == 200 || res?.StatutCode === undefined) {
                    alert.success(res?.Message || 'Layout enregistré');
                    loadScope(scope, codeFiliale, codeForm);
                    props.onSaved?.();
                } else alert.warning(res?.Message || 'Enregistrement refusé');
            })
            .catch((e: any) => { setSaving(false); alert.error('Error', e); });
    }

    function ConfirmDelete() {
        setSaving(true);
        const p: any = { Option: 'DELETE', CodeAbonne: user.CodeAbonne };
        if (layoutId) p.LayoutId = layoutId;
        else Object.assign(p, scopePayload(scope, codeFiliale, codeForm));
        postLayout(p)
            .then((res: any) => {
                setSaving(false);
                if (res?.StatutCode == 200 || res?.StatutCode === undefined) {
                    alert.success(res?.Message || 'Layout supprimé');
                    setOpenDelete(false);
                    loadScope(scope, codeFiliale, codeForm);
                } else alert.warning(res?.Message || 'Suppression refusée');
            })
            .catch((e: any) => { setSaving(false); alert.error('Error', e); });
    }

    /* ------------------------------ Montage ------------------------------ */

    /** Lignes affichées par zone : celles utilisées + une ligne vide supplémentaire. */
    const lignesOf = (zone: Zone): number[] => {
        const used = Array.from(new Set(champs.filter(c => c.Zone === zone).map(c => c.Ligne))).sort((a, b) => a - b);
        return [...used, (used[used.length - 1] || 0) + 1];
    };

    const cellChamps = (zone: Zone, ligne: number, align: Align) =>
        champs.map((c, i) => ({ c, i })).filter(({ c }) => c.Zone === zone && c.Ligne === ligne && c.Alignement === align);

    const renderCell = (zone: Zone, ligne: number, align: Align, actif: boolean) => (
        <div
            key={align}
            className={`flex-1 min-h-[32px] flex flex-wrap items-center gap-1 px-1 py-0.5 rounded
                ${align === 'C' ? 'justify-center' : align === 'D' ? 'justify-end' : 'justify-start'}
                ${actif ? 'hover:bg-[var(--eb-primary)]/5' : ''}`}
        >
            {cellChamps(zone, ligne, align).map(({ c, i }) => (
                <ChampChip key={i} c={c} selected={editIdx === i} onClick={() => setEditIdx(i)} />
            ))}
            {actif && (
                <button
                    type="button"
                    title={`Ajouter un élément — ${zone === 'HEADER' ? 'en-tête' : 'pied'} ligne ${ligne}, ${ALIGN_LABEL[align].toLowerCase()}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        setEditIdx(null);
                        setAddSlot(addSlot?.zone === zone && addSlot?.ligne === ligne && addSlot?.align === align ? null : { zone, ligne, align });
                    }}
                    className="w-5 h-5 inline-flex items-center justify-center rounded border border-dashed border-[var(--eb-border)] text-[var(--eb-text-muted)] hover:border-[var(--eb-primary)] hover:text-[var(--eb-primary)] transition-colors"
                >
                    <Plus size={10} />
                </button>
            )}
        </div>
    );

    const renderZone = (zone: Zone, actif: boolean, setActif: (v: boolean) => void) => (
        <div className={actif ? '' : 'opacity-40'}>
            <div className="flex items-center justify-between px-1.5 py-1">
                <span className="text-[9px] font-bold uppercase tracking-widest text-[var(--eb-text-muted)]">
                    {zone === 'HEADER' ? 'En-tête' : 'Pied de page'}
                </span>
                <Switch checked={actif} onChange={setActif} />
            </div>
            {lignesOf(zone).map(l => (
                <div key={l} className="flex border-t border-dashed border-[var(--eb-border)]/70">
                    {renderCell(zone, l, 'G', actif)}
                    <span className="w-px bg-[var(--eb-border)]/40" />
                    {renderCell(zone, l, 'C', actif)}
                    <span className="w-px bg-[var(--eb-border)]/40" />
                    {renderCell(zone, l, 'D', actif)}
                </div>
            ))}
        </div>
    );

    const editing = editIdx != null ? champs[editIdx] : null;
    const resolvedScope = resolved?.Scope || resolved?.Layout?.Scope;

    return (
        <>
            <FormDrawer
                open={props.open}
                onClose={props.onClose}
                title={`Mise en page — ${props.form?.Libelle || codeForm || props.layout?.Libelle || 'nouveau layout'}`}
                hint="Composez l'en-tête et le pied de page directement sur la page : cliquez sur une case pour y déposer un élément, cliquez l'élément pour l'éditer."
                width={600}
                footer={
                    <>
                        {layoutId != null && (
                            <Button variant="danger" onClick={() => setOpenDelete(true)} startIcon={<Trash2 size={15} />}>Supprimer</Button>
                        )}
                        <div className="flex-1" />
                        <Button variant="outline" onClick={() => setPreviewOpen(true)} startIcon={<Eye size={15} />}>Aperçu</Button>
                        <Button variant="outline" onClick={props.onClose}>Fermer</Button>
                        <Button variant="primary" onClick={handleSave} loading={saving} startIcon={<Save size={15} />}>Enregistrer</Button>
                    </>
                }
            >
                <Spin spinning={loading}>
                    {/* Portée — segmented */}
                    <div className="mb-3">
                        <div className="flex items-center justify-between mb-1.5">
                            <span className="text-xs font-semibold text-[var(--eb-text-muted)] uppercase tracking-wide">Portée du layout</span>
                            {resolvedScope && resolvedScope !== 'FORM' && scope === 'FORM' && (
                                <span className="text-[10px] text-[var(--eb-text-muted)]">
                                    Actuellement hérité : <Badge color="blue">{resolvedScope}</Badge>
                                </span>
                            )}
                        </div>
                        <div className="flex rounded-lg border border-[var(--eb-border)] overflow-hidden">
                            {SCOPES.map(s => (
                                <button
                                    key={s.value}
                                    type="button"
                                    onClick={() => { setScope(s.value); loadScope(s.value, codeFiliale, codeForm); }}
                                    title={s.hint}
                                    className={`flex-1 px-3 py-2 text-xs font-medium transition-colors
                                        ${scope === s.value ? 'bg-[var(--eb-primary)] text-white' : 'text-[var(--eb-text-muted)] hover:bg-[var(--eb-bg)]'}`}
                                >
                                    {s.label}
                                </button>
                            ))}
                        </div>
                        <div className="text-[10px] text-[var(--eb-text-muted)] mt-1">{SCOPES.find(s => s.value === scope)?.hint}</div>
                        {scope === 'FORM' && !props.form && (
                            <div className="mt-2">
                                <Select
                                    label="Formulaire *"
                                    value={codeForm}
                                    onChange={(v: string) => { setCodeForm(v); loadScope('FORM', codeFiliale, v); }}
                                    options={[
                                        { value: '', label: '—' },
                                        ...props.formulaires.map((f: any) => ({ value: f.CodeForm, label: `${f.CodeForm} - ${f.Libelle || ''}` })),
                                    ]}
                                />
                            </div>
                        )}
                        {scope === 'FILIALE' && (
                            <div className="mt-2">
                                <Select
                                    label="Filiale *"
                                    value={codeFiliale}
                                    onChange={(v: string) => { setCodeFiliale(v); loadScope('FILIALE', v, codeForm); }}
                                    options={[
                                        { value: '', label: '—' },
                                        ...props.filiales.map((f: any) => ({ value: f.CodeFiliale, label: `${f.CodeFiliale} - ${f.Libelle || ''}` })),
                                    ]}
                                />
                            </div>
                        )}
                        {scope === 'FORM' && codeForm && resolvedScope && resolvedScope !== 'FORM' && (
                            <div className="text-[11px] text-[var(--eb-text-muted)] mt-1.5">
                                Ce formulaire hérite actuellement du layout <Badge color="blue">{resolvedScope}</Badge> — enregistrer ici crée un layout FORM prioritaire.
                            </div>
                        )}
                    </div>

                    {/* Canevas — la page */}
                    <div className="rounded-xl border border-[var(--eb-border)] bg-[var(--eb-bg)]/60 p-3" onClick={() => { setEditIdx(null); setAddSlot(null); }}>
                        <div className="rounded-lg bg-white border border-[var(--eb-border)] shadow-sm overflow-hidden" onClick={(e) => e.stopPropagation()}>
                            {renderZone('HEADER', headerActif, setHeaderActif)}
                            <div className="px-3 py-16 text-center text-[10px] italic text-[var(--eb-text-muted)] bg-[var(--eb-bg)]/30 border-y border-dashed border-[var(--eb-border)]/70">
                                — Corps du formulaire —
                            </div>
                            {renderZone('FOOTER', footerActif, setFooterActif)}
                        </div>
                    </div>

                    {/* Palette — dépose d'un élément dans la case cliquée */}
                    {addSlot && (
                        <div className="mt-3 p-2.5 rounded-lg border border-[var(--eb-primary)]/30 bg-[var(--eb-primary-soft)]/30">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-[10px] font-bold uppercase tracking-wide text-[var(--eb-primary)]">
                                    Ajouter — {addSlot.zone === 'HEADER' ? 'en-tête' : 'pied'} · ligne {addSlot.ligne} · {ALIGN_LABEL[addSlot.align].toLowerCase()}
                                </span>
                                <IconButton type="button" size={22} tooltip="Fermer" onClick={() => setAddSlot(null)}><X size={12} /></IconButton>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {LAYOUT_TYPES.map(t => (
                                    <button
                                        key={t.value}
                                        type="button"
                                        onClick={() => addElement(addSlot.zone, addSlot.ligne, addSlot.align, t.value)}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-[var(--eb-border)] bg-[var(--eb-surface)] text-xs font-medium text-[var(--eb-text)] hover:border-[var(--eb-primary)] hover:text-[var(--eb-primary)] transition-colors"
                                    >
                                        {t.icon}{t.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Panneau de propriétés de l'élément sélectionné */}
                    {editing && editIdx != null && (
                        <div className="mt-3 p-3 rounded-lg border border-[var(--eb-primary)]/30 bg-[var(--eb-surface)]">
                            <div className="flex items-center justify-between mb-2">
                                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[var(--eb-text)]">
                                    {typeMeta(editing.TypeChamp).icon}
                                    {typeMeta(editing.TypeChamp).label}
                                    <span className="text-[10px] font-normal text-[var(--eb-text-muted)]">
                                        — {editing.Zone === 'HEADER' ? 'en-tête' : 'pied'}, ligne {editing.Ligne}, {ALIGN_LABEL[editing.Alignement].toLowerCase()}
                                    </span>
                                </span>
                                <div className="flex items-center gap-1">
                                    <IconButton type="button" size={24} color="danger" tooltip="Retirer l'élément" onClick={() => removeChamp(editIdx)}>
                                        <Trash2 size={13} />
                                    </IconButton>
                                    <IconButton type="button" size={24} tooltip="Fermer" onClick={() => setEditIdx(null)}>
                                        <X size={13} />
                                    </IconButton>
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                                <Select label="Type" value={editing.TypeChamp} searchable={false}
                                    onChange={(v: string) => updateChamp(editIdx, { TypeChamp: v, Valeur: defaultValeur(v) || editing.Valeur })}
                                    options={LAYOUT_TYPES.map(t => ({ value: t.value, label: t.label }))} />
                                <Input type='number' label="Ligne" min={1} value={editing.Ligne}
                                    onChange={(e: any) => updateChamp(editIdx, { Ligne: Math.max(1, Number(e.target.value) || 1) })} />
                                <Select label="Alignement" value={editing.Alignement} searchable={false}
                                    onChange={(v: string) => updateChamp(editIdx, { Alignement: v as Align })}
                                    options={[{ value: 'G', label: 'Gauche' }, { value: 'C', label: 'Centre' }, { value: 'D', label: 'Droite' }]} />
                            </div>
                            {typeMeta(editing.TypeChamp).valeur === 'input' && (
                                <div className="mt-2">
                                    <Input type='text' label="Texte" placeholder="Ex : Ma Banque SA"
                                        value={editing.Valeur} onChange={(e: any) => updateChamp(editIdx, { Valeur: e.target.value })} />
                                </div>
                            )}
                            {typeMeta(editing.TypeChamp).valeur === 'select-tenant' && (
                                <div className="mt-2">
                                    <Select label="Information" value={editing.Valeur || 'LIBELLE'} searchable={false}
                                        onChange={(v: string) => updateChamp(editIdx, { Valeur: v })}
                                        options={TENANT_VALEURS} />
                                </div>
                            )}
                            {typeMeta(editing.TypeChamp).valeur === 'select-image' && (
                                <div className="mt-2 space-y-2">
                                    <Select label="Source" value={editing.Valeur === 'LOGO' || !editing.Valeur ? 'LOGO' : '__CUSTOM__'} searchable={false}
                                        onChange={(v: string) => updateChamp(editIdx, { Valeur: v === '__CUSTOM__' ? '' : 'LOGO' })}
                                        options={[{ value: 'LOGO', label: 'Logo de l\'abonné' }, { value: '__CUSTOM__', label: 'Chemin / Base64 personnalisé…' }]} />
                                    {editing.Valeur !== 'LOGO' && (
                                        <Input type='text' label="Chemin ou Base64" placeholder="assets/logo.png ou base64…"
                                            value={editing.Valeur === '__CUSTOM__' ? '' : editing.Valeur}
                                            onChange={(e: any) => updateChamp(editIdx, { Valeur: e.target.value })} />
                                    )}
                                </div>
                            )}
                            {typeMeta(editing.TypeChamp).valeur === 'fixed' && (
                                <div className="mt-2 text-[11px] text-[var(--eb-text-muted)]">
                                    Valeur : <code>REF</code> — QR contenant CodeDemande / CodeForm.
                                </div>
                            )}
                        </div>
                    )}
                </Spin>
            </FormDrawer>

            <Modal
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                title="Aperçu — mise en page du PDF"
                width={640}
            >
                <div className="rounded-xl border border-[var(--eb-primary)]/25 bg-[var(--eb-primary-soft)]/30 p-4">
                    <div className="rounded-lg bg-white border border-[var(--eb-border)] shadow-sm overflow-hidden">
                        <div className="border-b border-[var(--eb-border)]/60">
                            <StaticZone champs={champs} zone="HEADER" actif={headerActif} />
                        </div>
                        <div className="px-4 py-24 text-center bg-[var(--eb-bg)]/30">
                            <div className="text-xs font-semibold text-[var(--eb-text)]">{props.form?.Libelle || codeForm || props.layout?.Libelle || 'Formulaire'}</div>
                            <div className="text-[10px] italic text-[var(--eb-text-muted)] mt-1">— Corps du formulaire (champs + réponses) —</div>
                        </div>
                        <div className="border-t border-[var(--eb-border)]/60">
                            <StaticZone champs={champs} zone="FOOTER" actif={footerActif} />
                        </div>
                    </div>
                </div>
                <p className="text-[11px] text-[var(--eb-text-muted)] mt-2 text-center">
                    Rendu schématique — le PDF réel est généré par le serveur (QuestPDF).
                </p>
            </Modal>

            <Modal
                open={openDelete}
                onClose={() => setOpenDelete(false)}
                title="Supprimer le layout"
                footer={
                    <>
                        <Button variant="outline" onClick={() => setOpenDelete(false)}>Annuler</Button>
                        <Button variant="danger" loading={saving} onClick={ConfirmDelete} startIcon={<Trash2 size={15} />}>Supprimer</Button>
                    </>
                }
            >
                <p className="text-sm text-[var(--eb-text)]">
                    Désactiver ce layout ? Le formulaire retombera sur l'héritage (filiale, puis abonné).
                </p>
            </Modal>
        </>
    );
}
